import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Eye, EyeOff, Shield, Heart } from "lucide-react";

const PASSWORD_KEY = "site_password";
const AUTH_KEY = "site_authenticated";
const DEFAULT_PASSWORD = "030618";

interface PasswordGateProps {
  children: React.ReactNode;
}

export default function PasswordGate({ children }: PasswordGateProps) {
  const [inputPassword, setInputPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = localStorage.getItem(AUTH_KEY);
    if (auth === "true") {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleLogin = () => {
    if (inputPassword === DEFAULT_PASSWORD) {
      localStorage.setItem(AUTH_KEY, "true");
      setIsAuthenticated(true);
      setError("");
    } else {
      setError("密码错误，请重试");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #2D1B4E 0%, #4A2D5E 50%, #7B4578 100%)" }}>
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Heart className="w-8 h-8" style={{ color: "oklch(0.85 0.14 55)" }} fill="oklch(0.85 0.14 55 / 0.3)" />
        </motion.div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #2D1B4E 0%, #4A2D5E 30%, #7B4578 60%, #B55B75 100%)",
      }}
    >
      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 2 + Math.random() * 3,
              height: 2 + Math.random() * 3,
              left: `${5 + Math.random() * 90}%`,
              top: `${10 + Math.random() * 80}%`,
              background: `oklch(${0.7 + Math.random() * 0.2} 0.1 ${55 + Math.random() * 20} / ${0.2 + Math.random() * 0.2})`,
            }}
            animate={{
              y: [0, -20 - Math.random() * 30, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full max-w-md rounded-2xl overflow-hidden relative z-10"
        style={{
          backgroundColor: "oklch(1 0 0 / 0.96)",
          backdropFilter: "blur(24px) saturate(1.4)",
          WebkitBackdropFilter: "blur(24px) saturate(1.4)",
          boxShadow: "0 32px 64px oklch(0.15 0.04 45 / 0.35), 0 0 0 1px oklch(1 0 0 / 0.1)",
          border: "1px solid oklch(1 0 0 / 0.2)",
        }}
      >
        {/* Header */}
        <div
          className="text-center relative overflow-hidden"
          style={{
            padding: "var(--spacing-2xl) var(--spacing-xl) var(--spacing-lg)",
            background: "linear-gradient(135deg, oklch(0.62 0.18 35) 0%, oklch(0.55 0.16 32) 50%, oklch(0.5 0.14 30) 100%)",
          }}
        >
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '20px 20px',
          }} />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="relative z-10 flex items-center justify-center rounded-full mx-auto mb-4"
            style={{
              width: 68,
              height: 68,
              backgroundColor: "oklch(1 0 0 / 0.15)",
              border: "1px solid oklch(1 0 0 / 0.2)",
              boxShadow: "0 8px 24px oklch(0.35 0.1 35 / 0.2)",
            }}
          >
            <Shield className="w-8 h-8" style={{ color: "oklch(0.98 0.01 70)" }} />
          </motion.div>
          <h1
            className="font-bold relative z-10"
            style={{
              fontSize: "var(--font-size-title)",
              color: "oklch(0.98 0.01 70)",
              fontFamily: "var(--font-handwritten)",
            }}
          >
            欢迎回来
          </h1>
          <p className="relative z-10" style={{ fontSize: "var(--font-size-label)", color: "oklch(0.92 0.04 65 / 0.85)", marginTop: "var(--spacing-xs)" }}>
            输入密码查看旅行足迹
          </p>
        </div>

        {/* Form */}
        <div style={{ padding: "var(--spacing-xl)" }}>
          <div className="space-y-4">
            {/* Password Input */}
            <div>
              <label
                className="block mb-2"
                style={{ fontSize: "var(--font-size-label)", color: "var(--foreground)", fontWeight: 500 }}
              >
                <Lock className="w-4 h-4 inline mr-2" style={{ color: "oklch(0.62 0.18 35)" }} />
                输入密码
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={inputPassword}
                  onChange={(e) => { setInputPassword(e.target.value); setError(""); }}
                  onKeyDown={handleKeyDown}
                  className="w-full rounded-xl outline-none transition-all duration-200"
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-md)",
                    paddingRight: 48,
                    fontSize: "var(--font-size-body)",
                    border: error
                      ? "1.5px solid oklch(0.65 0.18 25 / 0.5)"
                      : "1.5px solid oklch(0.90 0.025 65 / 0.6)",
                    backgroundColor: "oklch(0.98 0.01 70)",
                  }}
                  onFocus={(e) => {
                    if (!error) {
                      e.currentTarget.style.borderColor = "oklch(0.62 0.18 35 / 0.5)";
                      e.currentTarget.style.boxShadow = "0 0 0 3px oklch(0.62 0.18 35 / 0.1)";
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = error
                      ? "oklch(0.65 0.18 25 / 0.5)"
                      : "oklch(0.90 0.025 65 / 0.6)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  placeholder="请输入密码"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors hover:bg-muted"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  className="rounded-xl text-center"
                  style={{
                    padding: "var(--spacing-sm)",
                    backgroundColor: "oklch(0.65 0.18 25 / 0.08)",
                    color: "oklch(0.65 0.18 25)",
                    fontSize: "var(--font-size-label)",
                    border: "1px solid oklch(0.65 0.18 25 / 0.15)",
                  }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLogin}
              className="w-full rounded-xl transition-shadow duration-300"
              style={{
                padding: "var(--spacing-sm) var(--spacing-md)",
                background: "linear-gradient(135deg, oklch(0.62 0.18 35) 0%, oklch(0.56 0.17 32) 100%)",
                color: "white",
                fontSize: "var(--font-size-body)",
                fontWeight: 600,
                boxShadow: "0 4px 16px oklch(0.55 0.15 35 / 0.3)",
                border: "1px solid oklch(0.65 0.18 35 / 0.3)",
              }}
            >
              解锁
            </motion.button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
              忘记密码？清除浏览器数据可重置
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
